import type { IInvestmentProposalRepository } from "@/application/ports/repositories";
import type { InvestmentResponse, InvestmentTermsRequest } from "@/domain/investment/types";
import type {
  InvestmentProposalCreateRequest,
  InvestmentProposalResponse,
  ProposalListParams,
  ProposalListResult,
  ProposalSort,
} from "@/domain/investmentProposal";
import * as sdk from "../generated/sdk.gen";
import { unwrap } from "../unwrap";

const many = <T>(values: readonly T[] | undefined) =>
  values && values.length > 0 ? [...values] : undefined;

const toSortQuery = (sort: readonly ProposalSort[] | undefined) =>
  sort && sort.length > 0
    ? sort.map((entry) => `${entry.key}:${entry.direction}`).join(",")
    : undefined;

export class InvestmentProposalRepository implements IInvestmentProposalRepository {
  async createProposal(data: InvestmentProposalCreateRequest): Promise<InvestmentProposalResponse> {
    return unwrap(sdk.createProposal({ body: data }));
  }

  async getProposals(
    params: ProposalListParams,
    signal?: AbortSignal,
  ): Promise<ProposalListResult> {
    const { sort, limit, offset, investor_uuid, status, fiscal_year } = params;
    const { items, ...rest } = await unwrap(
      sdk.listProposals({
        query: {
          investor_uuid: many(investor_uuid),
          status: many(status),
          fiscal_year: fiscal_year === undefined ? undefined : [fiscal_year],
          sort: toSortQuery(sort),
          limit,
          offset,
        },
        signal,
      }),
    );
    return { proposals: items, ...rest };
  }

  async getProposal(
    uuid: string,
    token?: string | null,
    signal?: AbortSignal,
  ): Promise<InvestmentProposalResponse> {
    return unwrap(
      sdk.getProposal({
        path: { proposal_uuid: uuid },
        query: token ? { token } : undefined,
        signal,
      }),
    );
  }

  async resendProposal(uuid: string): Promise<InvestmentProposalResponse> {
    return unwrap(sdk.resendProposal({ path: { proposal_uuid: uuid } }));
  }

  async deleteProposal(uuid: string): Promise<void> {
    await unwrap(sdk.deleteProposal({ path: { proposal_uuid: uuid } }));
  }

  async acceptProposal(uuid: string, token: string): Promise<void> {
    await unwrap(sdk.acceptProposal({ path: { proposal_uuid: uuid }, query: { token } }));
  }

  async declineProposal(uuid: string, token: string): Promise<void> {
    await unwrap(sdk.declineProposal({ path: { proposal_uuid: uuid }, query: { token } }));
  }

  async createInvestment(uuid: string, terms: InvestmentTermsRequest): Promise<InvestmentResponse> {
    return unwrap(sdk.createInvestmentFromProposal({ path: { proposal_uuid: uuid }, body: terms }));
  }
}
